import { useState } from 'react';
import { RefreshCcw } from 'lucide-react';
import { Button, EmptyState, ErrorState, Input, toast } from '@helios/ui';
import { useAuth } from '@/features/auth/authContext';
import { LoadingRow, SettingsStatus, selectClassName } from '@/features/settings/settingsPanels';
import {
  SettingsPageFrame,
  SettingsField as Field,
  SettingsSection,
  SettingsTableScroll,
  settingsFilterActionClass,
  settingsFilterRowClass,
} from '@/features/settings/settingsChrome';
import { useUserPreferences } from '@/features/settings/UserPreferencesProvider';
import { useAdminTenantMutations, useAdminTenants } from '@/hooks/useAdmin';
import type { AdminRecordStatus, AdminTenantItem } from '@/services/adminClient';
import { getSessionOriginKey } from '@/services/sessionOrigin';
import { SubscriberInvitationsPanel } from './SubscriberInvitationsPanel';

type TenantDraft = {
  baseline: {
    name: string;
    status: AdminRecordStatus;
    plan: string;
    licensedUserLimit: number | null;
    version: number;
  };
  name: string;
  status: AdminRecordStatus;
  plan: string;
  licensedUserLimit: string;
  reviewReason: string;
};
const snapshot = (tenant: AdminTenantItem): TenantDraft => ({
  baseline: {
    name: tenant.name,
    status: tenant.status as AdminRecordStatus,
    version: tenant.version,
    plan: tenant.plan ?? '',
    licensedUserLimit: tenant.licensedUserLimit ?? null,
  },
  name: tenant.name,
  status: tenant.status as AdminRecordStatus,
  plan: tenant.plan ?? '',
  licensedUserLimit: tenant.licensedUserLimit == null ? '' : String(tenant.licensedUserLimit),
  reviewReason: '',
});
const isDirty = (draft: TenantDraft) =>
  draft.name !== draft.baseline.name ||
  draft.status !== draft.baseline.status ||
  draft.plan !== draft.baseline.plan ||
  draft.licensedUserLimit !==
    (draft.baseline.licensedUserLimit == null ? '' : String(draft.baseline.licensedUserLimit));

export function SettingsTenantsPage() {
  const auth = useAuth();
  const scope = JSON.stringify([
    auth.user?.id,
    auth.tenant?.id,
    [...new Set(auth.permissions)].sort(),
    auth.sessionId,
    auth.authVersion,
    auth.accessMode,
    getSessionOriginKey(),
  ]);
  return <TenantsWorkspace key={scope} />;
}

function TenantsWorkspace() {
  const auth = useAuth();
  const [operator, setOperator] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<AdminRecordStatus | ''>('');
  const [drafts, setDrafts] = useState<Record<string, TenantDraft>>({});
  const tenants = useAdminTenants({
    page,
    pageSize: 25,
    q: q || undefined,
    status: status || undefined,
  });
  const updateTenant = useAdminTenantMutations();
  const items = tenants.data?.items ?? [];
  const meta = tenants.data?.meta;
  const replaceDraft = (id: string, draft: TenantDraft) =>
    setDrafts((current) => ({ ...current, [id]: draft }));

  return (
    <SettingsPageFrame
      title="Subscribers & tenants"
      description="Invite company owners, manage onboarding, and maintain customer workspaces."
      actions={
        <Button type="button" variant="outline" size="xs" onClick={() => void tenants.refetch()}>
          <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
          Refresh
        </Button>
      }
    >
      {auth.authenticated ? <SubscriberInvitationsPanel onOperatorChange={setOperator} /> : null}
      <SettingsStatus
        message={null}
        error={tenants.error instanceof Error ? tenants.error : null}
      />
      <SettingsSection
        title="Workspaces"
        description="Platform-admin only. Your own tenant status is locked. Drafts survive paging and refresh until saved or explicitly reverted."
      >
        <form
          className={`${settingsFilterRowClass} mb-2 md:grid-cols-3`}
          onSubmit={(event) => {
            event.preventDefault();
            setQ(search.trim());
            setPage(1);
          }}
        >
          <Field label="Search">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name or slug"
            />
          </Field>
          <Field label="Status">
            <select
              className={selectClassName}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as AdminRecordStatus | '');
                setPage(1);
              }}
            >
              <option value="">Any</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </Field>
          <div className={settingsFilterActionClass}>
            <Button type="submit" size="sm" variant="outline">
              Apply
            </Button>
          </div>
        </form>
        {tenants.isLoading ? (
          <LoadingRow label="Loading tenants…" />
        ) : tenants.isError ? (
          <ErrorState
            title="Tenants unavailable"
            description="The admin tenants API did not respond."
          />
        ) : items.length === 0 ? (
          <EmptyState title="No tenants" description="No workspaces matched this filter." />
        ) : (
          <SettingsTableScroll label="Tenant directory">
            <table className="w-full min-w-[940px] border-collapse text-left text-xs">
              <thead className="bg-card text-2xs text-muted-foreground uppercase tracking-wide">
                <tr>
                  {[
                    'Tenant',
                    'Plan & seats',
                    'Users',
                    'Projects',
                    'Status',
                    'Updated',
                    'Actions',
                  ].map((label) => (
                    <th key={label} className="border-border border-b px-3 py-2 font-medium">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((tenant) => (
                  <TenantRow
                    key={tenant.id}
                    tenant={tenant}
                    draft={drafts[tenant.id]}
                    isOwn={tenant.id === auth.tenant?.id}
                    canReviewAllowance={operator}
                    onDraft={(draft) => replaceDraft(tenant.id, draft)}
                    onSave={async (input) => {
                      const updated = await updateTenant.mutateAsync({ id: tenant.id, input });
                      replaceDraft(tenant.id, snapshot(updated));
                      toast.success('Tenant updated');
                    }}
                    onReload={async () => {
                      const result = await tenants.refetch();
                      if (result.isError) throw result.error ?? new Error('Reload failed');
                      const fresh = result.data?.items.find((item) => item.id === tenant.id);
                      if (!fresh)
                        throw new Error(
                          'Tenant is outside the current page or filter. Change the filter and reopen this workspace.',
                        );
                      replaceDraft(tenant.id, snapshot(fresh));
                    }}
                  />
                ))}
              </tbody>
            </table>
          </SettingsTableScroll>
        )}
        <div className="text-muted-foreground mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
          <span>
            {meta ? `${meta.total} tenants · ` : ''}Page {page} of{' '}
            {Math.max(1, meta?.totalPages ?? 1)}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              size="xs"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((value) => value - 1)}
            >
              Previous
            </Button>
            <Button
              type="button"
              size="xs"
              variant="outline"
              disabled={!meta || page >= meta.totalPages}
              onClick={() => setPage((value) => value + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </SettingsSection>
    </SettingsPageFrame>
  );
}

function TenantRow({
  tenant,
  draft,
  isOwn,
  canReviewAllowance,
  onDraft,
  onSave,
  onReload,
}: {
  tenant: AdminTenantItem;
  draft?: TenantDraft;
  isOwn: boolean;
  canReviewAllowance: boolean;
  onDraft: (draft: TenantDraft) => void;
  onSave: (input: {
    name?: string;
    status?: AdminRecordStatus;
    plan?: string | null;
    licensedUserLimit?: number;
    reviewReason?: string;
    expectedVersion: number;
  }) => Promise<void>;
  onReload: () => Promise<void>;
}) {
  const { formatDateTime } = useUserPreferences();
  const current =
    draft && (isDirty(draft) || draft.baseline.version >= tenant.version)
      ? draft
      : snapshot(tenant);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = isDirty(current);
  const allowanceDirty =
    current.plan !== current.baseline.plan ||
    current.licensedUserLimit !==
      (current.baseline.licensedUserLimit == null
        ? ''
        : String(current.baseline.licensedUserLimit));
  const allowanceValid =
    !allowanceDirty ||
    (canReviewAllowance &&
      current.reviewReason.trim().length >= 15 &&
      (current.licensedUserLimit === ''
        ? current.baseline.licensedUserLimit === null
        : Number.isInteger(Number(current.licensedUserLimit)) &&
          Number(current.licensedUserLimit) >= 1 &&
          Number(current.licensedUserLimit) <= 500));
  const conflict = dirty && tenant.version > current.baseline.version;
  const run = async (action: () => Promise<void>) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  };
  return (
    <tr className="border-border/70 border-b last:border-b-0">
      <td className="px-2 py-2">
        <Input
          aria-label={`Tenant name ${tenant.slug}`}
          minLength={2}
          maxLength={160}
          disabled={saving}
          value={current.name}
          onChange={(event) => onDraft({ ...current, name: event.target.value })}
        />
        <p className="text-2xs text-muted-foreground mt-1">
          {tenant.slug} · version {current.baseline.version}
          {dirty ? ' · Unsaved' : ''}
        </p>
      </td>
      <td className="text-muted-foreground px-2 py-2">
        {canReviewAllowance ? (
          <div className="grid gap-2">
            <Input
              aria-label={`Plan for ${tenant.slug}`}
              maxLength={80}
              value={current.plan}
              disabled={saving}
              onChange={(event) => onDraft({ ...current, plan: event.target.value })}
              placeholder="No plan label"
            />
            <Input
              aria-label={`Seat allowance for ${tenant.slug}`}
              type="number"
              min={1}
              max={500}
              value={current.licensedUserLimit}
              disabled={saving}
              onChange={(event) => onDraft({ ...current, licensedUserLimit: event.target.value })}
              placeholder="Legacy unlimited"
            />
            <p className="text-2xs">
              Seat allowance includes the owner; editing does not create a billing charge.
            </p>
            {allowanceDirty ? (
              <textarea
                aria-label={`Review reason for ${tenant.slug}`}
                minLength={15}
                maxLength={1000}
                className={selectClassName}
                value={current.reviewReason}
                onChange={(event) => onDraft({ ...current, reviewReason: event.target.value })}
                placeholder="Reason for this plan or seat review"
              />
            ) : null}
          </div>
        ) : (
          <>
            {tenant.plan ?? '—'}
            <p className="text-2xs">Seats: {tenant.licensedUserLimit ?? 'Legacy unlimited'}</p>
          </>
        )}
      </td>
      <td className="text-muted-foreground px-2 py-2">{tenant.userCount}</td>
      <td className="text-muted-foreground px-2 py-2">{tenant.projectCount}</td>
      <td className="px-2 py-2">
        <select
          aria-label={`Status for ${tenant.slug}`}
          className={selectClassName}
          value={current.status}
          disabled={isOwn || saving}
          onChange={(event) =>
            onDraft({ ...current, status: event.target.value as AdminRecordStatus })
          }
        >
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="ARCHIVED">Archived</option>
          {current.status === 'DELETED' ? (
            <option value="DELETED" disabled>
              Deleted
            </option>
          ) : null}
        </select>
        {isOwn ? (
          <p className="text-2xs text-muted-foreground mt-1">Own tenant status is locked</p>
        ) : null}
      </td>
      <td className="text-muted-foreground px-2 py-2">{formatDateTime(tenant.updatedAt)}</td>
      <td className="px-2 py-2">
        <div className="flex flex-wrap gap-1">
          <Button
            type="button"
            size="xs"
            variant="outline"
            aria-label={`Save ${tenant.slug}`}
            disabled={
              saving || !dirty || conflict || !allowanceValid || current.name.trim().length < 2
            }
            onClick={() =>
              void run(() =>
                onSave({
                  name: current.name.trim(),
                  status: isOwn ? undefined : current.status,
                  expectedVersion: current.baseline.version,
                  ...(current.plan !== current.baseline.plan
                    ? { plan: current.plan.trim() || null }
                    : {}),
                  ...(current.licensedUserLimit !==
                    (current.baseline.licensedUserLimit == null
                      ? ''
                      : String(current.baseline.licensedUserLimit)) &&
                  current.licensedUserLimit !== ''
                    ? { licensedUserLimit: Number(current.licensedUserLimit) }
                    : {}),
                  ...(allowanceDirty ? { reviewReason: current.reviewReason.trim() } : {}),
                }),
              )
            }
          >
            Save
          </Button>
          <Button
            type="button"
            size="xs"
            variant="outline"
            aria-label={`Revert ${tenant.slug}`}
            disabled={saving || !dirty}
            onClick={() => {
              onDraft(
                tenant.version >= current.baseline.version
                  ? snapshot(tenant)
                  : {
                      ...current,
                      name: current.baseline.name,
                      status: current.baseline.status,
                      plan: current.baseline.plan,
                      licensedUserLimit:
                        current.baseline.licensedUserLimit == null
                          ? ''
                          : String(current.baseline.licensedUserLimit),
                      reviewReason: '',
                    },
              );
              setError(null);
            }}
          >
            Revert
          </Button>
          <Button
            type="button"
            size="xs"
            variant="outline"
            aria-label={`Reload current ${tenant.slug}`}
            disabled={saving}
            onClick={() => void run(onReload)}
          >
            Reload current
          </Button>
        </div>
        {conflict ? (
          <p role="status" className="text-2xs text-warning mt-1 max-w-xs">
            Workspace changed. Your draft is preserved. Reload current or Revert before editing the
            new version.
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-critical mt-1 max-w-xs text-xs">
            {error}
          </p>
        ) : null}
      </td>
    </tr>
  );
}
